/* eslint-disable @next/next/no-img-element */
import React, {useEffect, useRef, useState} from 'react';
import {Button, Tooltip, Upload} from 'antd';
import styles from './style.module.scss';
import {AtomicBlockUtils, EditorState} from 'draft-js';
import {Input} from '@/components/common-components/v2';
import {useStore} from '@/store/root-store';
import {observer} from 'mobx-react';
import styled from 'styled-components';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCloudArrowUp, faTrashXmark} from '@fortawesome/pro-light-svg-icons';
import {CONTENT_ASSISTANT_API} from '@/api/ContentAssistant';
import {antdNotification} from '@/utils/notification';
import {NextImg} from '@/utils/nextImg';

const {Dragger} = Upload;

interface Props {
  updateText: (state: EditorState) => void;
  editorState: any;
  setShowNewUrlEditor: any;
}

export const BrowseImage = observer(({setShowNewUrlEditor, updateText, editorState}: Props) => {
  const {
    contentOptimizer: {
      currentPage: {currentEditorState},
    },
  } = useStore('');
  const [loader, setLoader] = useState(false);
  const [isError, setIsError] = useState('');
  const [editorValues, setEditorValues] = useState({
    urlValue: '',
    altValue: '',
    height: 0,
    width: 0,
  });
  const dateRef = useRef(null);

  // Close anchor form popup by clicking anywhere on the screen
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dateRef.current &&
        !dateRef.current.parentElement.contains(event.target)
      ) {
        setEditorValues({
          urlValue: '',
          altValue: '',
          height: 0,
          width: 0,
        });
        setIsError('');
        setImgFile(null);
        setImage(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dateRef]);

  const insertText = (img:any) => {
    const currentContent = currentEditorState.getCurrentContent();
    if (image) {
      const contentStateWithEntity = currentContent.createEntity(
        'customImage',
        'IMMUTABLE',
        {src: img, alt: editorValues.altValue, height: editorValues.height ? `${editorValues.height}%` : '100%', width: editorValues.width ? `${editorValues.width}%` : '100%'},
      );
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
      const newEditorState = EditorState.set(editorState, {
        currentContent: contentStateWithEntity,
      });
      return AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, ' ');
    }
    return editorState;
  };

  const sendTextToEditor = async () => {
    setLoader(true);
    const formData = new FormData();
    formData.append('image', imgFile);
    try {
      const res = await CONTENT_ASSISTANT_API.uploadImage(formData);
      setImage(res.data.image);
      const contentState = insertText(res.data.image);
      updateText(contentState);
      setEditorValues({
        urlValue: '',
        altValue: '',
        height: 0,
        width: 0,
      });
      setImgFile(null);
      setImage(null);
      setShowNewUrlEditor(false);
    } catch (e:any) {
      antdNotification.error('Something went wrong!');
      setShowNewUrlEditor(false);
    } finally {
      setLoader(false);
    }
  };

  const ele = document.getElementById('numberHeight');
  ele?.addEventListener('keydown', function(e) {
    const keyCode = e.which || e.keyCode;
    if (keyCode === 69 || keyCode === 187 || keyCode === 189) {
      e.preventDefault();
    }
  });

  const closeModal = () => {
    setImgFile(null);
    setImage(null);
    setEditorValues({
      urlValue: '',
      altValue: '',
      height: 0,
      width: 0,
    });
    setIsError('');
    setShowNewUrlEditor(false);
  };

  const [image, setImage] = useState(null);
  const [imgFile, setImgFile] = useState<File>();

  const handleImageChange = (selectedFile: any) => {
    setImgFile(selectedFile);
    if (selectedFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setImage(result);
      };
      reader.readAsDataURL(selectedFile);
      setEditorValues({
        ...editorValues,
        urlValue: image,
      });
    }
  };

  const deleteImage = () => {
    setImgFile(null);
    setImage(null);
  };

  const RequiredFileType = 'image/jpg,image/png,image/jpeg,image/svg';

  return (
    <div>
      <p style={{color: 'rgba(78, 81, 86, 1)', marginBottom: '10px'}}>Image source</p>
      {image ?
        <div style={{display: 'flex', alignItems: 'center', marginTop: 7}}>
          <div className='imageContainer'>
            <NextImg className= 'styledImage' src={`${image}`} alt='' />
          </div>
          <div style={{width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}><div style= {{marginLeft: 12, marginRight: 12}}>{imgFile?.name}</div>
            <Tooltip title='Remove image'><FontAwesomeIcon className='trashIcon' onClick={deleteImage} color='rgba(163, 164, 164, 1)' icon={faTrashXmark}/></Tooltip></div><br/>
        </div> :
        <StyleDragger isError={isError}
          multiple={false}
          showUploadList={false}
          style={{height: '40px'}}
          // disabled={urlUpload}
          customRequest={(data:any) => {
            // setDisableBtn(false);
            if (RequiredFileType.split(',').includes(data?.file?.type)) {
              setIsError('');
              handleImageChange(data.file);
            } else {
              setIsError('Wrong file format selected!');
            }
          }}
          onDrop={ e => {
            // setDisableBtn(false);
            const getFile = e.dataTransfer.files?.length ? e.dataTransfer.files[0] : null;
            if (RequiredFileType.split(',').includes(getFile?.type)) {
              setIsError('');
              handleImageChange(getFile);
            } else {
              setIsError('Wrong file format selected!');
            }
          } }
        >
          <div className='dragInner'>
            <FontAwesomeIcon icon={faCloudArrowUp} color='#1FAC47'/>
            <p>
                    Drag & Drop image here <br /> or <span>Click to browse</span>
            </p>
          </div>
        </StyleDragger> }
      {isError.length > 1 && <p style={{color: 'red'}}>{isError}</p>}
      <p>Alt tag (optional)</p>
      <InputWraper>
        <Input
          onMouseDown={(e: any) => e.stopPropagation()}
          onChange={(e: any) =>
            setEditorValues({
              ...editorValues,
              altValue: e.target.value,
            })
          }
          type='text'
          value={editorValues.altValue}
          placeholder=''
          className={styles.urlInput}
        />
      </InputWraper>
      <AtributeWrapper>
        <div>
          <p>Width (optional)</p>
          <InputWraper>
            <Input
              onMouseDown={(e: any) => e.stopPropagation()}
              onChange={(e: any) =>
                setEditorValues({
                  ...editorValues,
                  width: e.target.value < 101 && e.target.value !== 'e' ? e.target.value : e.target.value !== 'e' ? editorValues.width : 0,
                })
              }
              type='number'
              value={editorValues.width}
              placeholder=''
              max={100}
              min={0}
              className={styles.urlInput}
              id='numberHeight'
            />
          </InputWraper>
        </div>
        <div>
          <p>Height (optional)</p>
          <InputWraper>
            <Input
              onMouseDown={(e: any) => e.stopPropagation()}
              onChange={(e: any) =>
                setEditorValues({
                  ...editorValues,
                  height: e.target.value < 101 && e.target.value !== 'e' ? e.target.value : e.target.value !== 'e' ? editorValues.height : 0,
                })
              }
              type='number'
              value={editorValues.height}
              placeholder=''
              className={styles.urlInput}
              max={100}
              min={0}
              id='numberHeight'
            />
          </InputWraper>
        </div>
      </AtributeWrapper>
      <Pwrapper>
        <CancelBtn onClick={closeModal}>Cancel</CancelBtn>
        <AddBtn
          loading={loader}
          disabled={!imgFile || loader}
          onClick={sendTextToEditor}
        >
                Add to article
        </AddBtn>
      </Pwrapper>
    </div>
  );
});


const InputWraper = styled.div`
  margin-top: 10px;
  background-color: rgba(249, 249, 251, 1) !important;
  border-radius: 9px;
  .ant-input {
    background-color: transparent !important;
    border: none !important;
    margin-bottom: 0 !important;
    padding: 20px 8px 20px 11px !important;
  }
  .ant-input:focus,
  .ant-input-focused {
    box-shadow: none !important;
  }
  p {
    align-items: center;
    display: flex;
    padding: 0 17px 0 10px;
    border-left: 2px solid #c9c7c7;
    margin: 9px 0 7px 0;
    color: blue;
    cursor: pointer;
  }
`;

const Pwrapper = styled.div`
  text-align: initial;
  margin: 18px 0 0 8px;
  font-weight: 400;
  display: flex;
  justify-content: end;
  span {
    color: blue;
  }
  .ant-btn[disabled]{
    background-color: #2d6cca80 !important;
    span{
      color: white !important;
    }
  }

`;

const AtributeWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  .ant-input {
    width: 143px !important;
  }
`;

const CancelBtn = styled(Button)`
  background-color: transparent;
  color: black !important;
  height: 38px;
  border-radius: 9px;
  border: 1px solid lightgray !important;
  &:hover{
    background-color:white !important;
    color:black;
    border: 1px solid lightgray;

  }
  span{
    color: black;
  }
`;

const AddBtn = styled(Button)`
  margin-left: 15px;
  background-color: #2D6CCA;
  height: 38px;
  border-radius: 9px;
  span {
    color: white;
  }
  &:hover {
    background-color: #2D6CCA !important;
  }
  &:active {
    background-color: #2D6CCA !important;
  }
  &:focused {
    background-color: #2D6CCA !important;
  }
`;

const StyleDragger = styled(Dragger)<{isError?: any}>`
background-color: white !important;
height: 152px  !important;
border-radius: 10px  !important;
border-color: ${p=> p.isError?.length > 1 ? 'red' : ''} !important;
.dragInner{
  p{
    text-align: center;
    font-size: 12px;
    span{
      color: #2D6CCA;
    }
  }
  svg{
    height: 36px;
    color: #4E5156;
  }
}
&:hover{
border-color: ${p=> p.isError?.length > 1 ? 'red' : '#d9d9d9'} !important;
}
`;
